<script lang="ts">
  import Grid from "$lib/components/Grid/Grid.svelte";
  import Column from "$lib/components/Column/Column.svelte";
  import EditorialCard from "$lib/components/Cards/EditorialCard/EditorialCard.svelte";
  import Container from "$lib/components/Container/Container.svelte";
  import {
    type Link,
    type FliteImage,
    Position,
    Alignment,
  } from "$lib/gql/genCms/codegen";
  import Typography from "../Typography/Typography.svelte";
  import { processInlineLinks } from "$lib/utils/url";
  import inView from "$lib/utils/inView";
  import Button from "$lib/components/Button/Button.svelte";
  import TwoColumnLayout from "../TwoColumnLayout/TwoColumnLayout.svelte";

  export let title: string;
  export let text: string;
  export let subtitle: string;
  export let cta: Link;
  export let image: FliteImage;

  let ctaTextLight = cta?.textLight;
  let ctaBgColour = cta?.bgColour;
  let ctaText = cta?.text;
  let ctaHref = cta?.href;

  export let position: Position;
  export let alignment: Alignment;

  export let textPosition: Position;

  $: contentPosition = position.toLowerCase();
  $: contentAlignment = alignment.toLowerCase();
  $: contentTextPosition = textPosition.toLowerCase();

  export let reverse = false;

  let isMobile = false;
  let viewportWidth: number = 0;
  let mdWidth = 768;

  $: isMobile = viewportWidth < mdWidth;
  export let headingElement = "h2";
  export let headingVariant = "h1";

  export let subtitleElement = "h3";
  export let subtitleVariant = "button1";

  export let invert = false;

  let isInView = false;

  $: rightColumnProps = {
    smSpan: 4,
    mdSpan: 4,
    lgSpan: 6,
    xlSpan: 6,
    xxlSpan: 6,
    smStart: 1,
    mdStart: reverse ? 5 : 1, // start in 2nd half if reversed
    lgStart: reverse ? 7 : 1,
  };

  $: leftColumnProps = {
    smSpan: 4,
    mdSpan: 4,
    lgSpan: 6,
    xlSpan: 6,
    xxlSpan: 6,
    smStart: 1,
    mdStart: reverse ? 1 : 5, // start in 1st half if reversed
    lgStart: reverse ? 1 : 7,
  };
</script>

<style lang="scss">
  .article {
    position: relative;
    padding: 2rem 0;
    border-bottom: 1px solid $background;
    background-color: $inverse-on-surface !important;
    &--invert {
      background-color: $inverse-surface !important;
    }
    @include retina {
      border-bottom: 0.5px solid $background;
    }
  }

  .wrapper {
    padding: 0;
    margin-bottom: 0;
    grid-column: span 4;
  }

  .title + .description {
    margin-top: 13px;
  }
  .subtitle {
    margin-bottom: 1rem;
  }

  .title,
  .description,
  .cta {
    max-width: 100%;
    @include breakpoint(md) {
      max-width: 80%;
      &__centered {
        max-width: 100%;
      }
    }
  }

  .cta {
    margin-top: 2rem;
    display: flex;
    justify-content: start;
    align-items: center;
  }

  .html :global(p),
  .html :global(span) {
    padding: 0;
    margin: 0;
    color: $on-surface;
  }

  .html--invert :global(*) {
    color: $inverse-on-surface !important;
  }

  @include breakpoint(md) {
    .wrapper {
      position: relative;
      height: 100%;

      &--left {
        grid-column: auto / span 4;
      }

      &--center {
        grid-column: 3 / span 4;
      }

      &--right {
        grid-column: 5 / span 4;
      }
    }
    .wrapper-inner {
      position: relative;
      opacity: 0;
      height: 100%;
      display: flex;
      flex-direction: column;

      &--align-bottom {
        justify-content: end;
      }

      &--align-top {
        justify-content: start;
      }
      &--align-center {
        justify-content: center;
      }

      &--position-left {
        align-items: start;
      }
      &--position-center {
        align-items: center;
      }
      &--position-right {
        align-items: end;
      }

      &--text-left {
        text-align: left;
      }
      &--text-center {
        text-align: center;
      }
      &--text-right {
        text-align: right;
      }

      top: -10%;
      transition: $transition;
    }

    .isInView {
      opacity: 1;
      transition: $transition;

      transform: translateY(10%);
    }
  }

  @include breakpoint(lg) {
    .wrapper {
      margin-bottom: 64px;
      padding: 64px 0;

      &--left {
        grid-column: auto / span 6;
      }
      &--center {
        grid-column: 4 / span 6;
      }

      &--right {
        grid-column: 8 / span 6;
      }
    }
  }
</style>

<svelte:window bind:innerWidth={viewportWidth} />

<div
  class="article {invert ? 'article--invert' : 'article'}"
  data-cs-override-id="article"
  use:inView={{ threshold: 0.1 }}
  on:intersect={({ detail }) => {
    if (detail) {
      isInView = true;
    }
  }}
>
  <Container>
    <TwoColumnLayout {reverse}>
      <Column {...leftColumnProps} slot="left">
        <div class="wrapper">
          <div
            class="wrapper-inner wrapper-inner--align-{contentAlignment} wrapper-inner--position-{contentPosition} wrapper-inner--text-{contentTextPosition} isInView--{contentPosition}"
            class:isInView
          >
            {#if title}
              <div
                class={`title ${
                  contentPosition === "center" && "title__centered"
                }`}
              >
                {#if subtitle}
                  <div
                    class={`subtitle ${
                      contentPosition === "center" && "subtitle__centered"
                    }`}
                  >
                    <Typography
                      component={subtitleElement}
                      variant={subtitleVariant}
                      theme={invert ? "inverse-on-surface" : "on-surface"}
                      >{subtitle}</Typography
                    >
                  </div>
                {/if}
                <Typography
                  component={headingElement}
                  variant={headingVariant}
                  theme={invert ? "inverse-on-surface" : "on-surface"}
                  >{title}</Typography
                >
              </div>
            {/if}
            {#if text}
              <div
                class={`description ${
                  contentPosition === "center" && "description__centered"
                }`}
              >
                <Typography
                  component="span"
                  variant="body1"
                  theme={invert ? "inverse-on-surface" : "on-surface"}
                >
                  <div class={`html ${invert && "html--invert"}`}>
                    {@html processInlineLinks(text)}
                  </div>
                </Typography>
              </div>
            {/if}
            {#if cta}
              <div
                class={`cta ${contentPosition === "center" && "cta__centered"}`}
              >
                <Button
                  primary
                  href={ctaHref}
                  label={ctaText}
                  primaryInvert={invert}
                  theme={ctaBgColour !== "#" && ctaTextLight
                    ? "inverse-on-surface"
                    : ctaBgColour !== "#" && !ctaTextLight
                      ? "on-surface"
                      : invert
                        ? "on-surface"
                        : "inverse-on-surface"}
                  hoverTheme={!invert ? "on-surface" : "inverse-on-surface"}
                  backgroundColor={ctaBgColour !== "#"
                    ? ctaBgColour
                    : invert && ctaBgColour === "#"
                      ? "on-surface"
                      : "inverse-on-surface"}
                  hoverBgColor="transparent"
                />
              </div>
            {/if}
          </div>
        </div>
      </Column>
      <Column {...rightColumnProps} slot="right">
        <EditorialCard {image} twoImageHero {invert} />
      </Column>
    </TwoColumnLayout>
  </Container>
</div>
